import { createClient } from "@/app/utils/supabase/clients";
import posthog from "posthog-js";
import {
  CACHE_ENDPOINT,
  CacheRequestMetadata,
  FetchSeriesResult,
  RawSeriesResult,
  SeriesCacheRequest,
  SeriesEmbedScope,
  SeriesResult,
  buildCacheKey,
  buildErrorResult,
  cacheServiceHeaders,
  isFailedResult,
  normalizeRequests,
  parseCacheResponse,
} from "@/lib/cache/seriesCacheMappers";

export type { SeriesResult, SeriesCacheRequest, CacheRequestMetadata, FetchSeriesResult, SeriesEmbedScope };

/**
 * Our own route for public links and signed-out viewers: the server attaches
 * the embed service account and checks the request against the link. Signed-in
 * viewers in the app call the cache service directly (see `routeFor`).
 */
const SERIES_ENDPOINT = "/api/series";

/**
 * Client-side series cache.
 *
 * Every request is cached on its own, keyed by `entryKey`, no matter how it was
 * fetched. A dashboard batch therefore also answers each of its gauges' single
 * lookups, and a gauge can never miss data that a batch already asked for.
 *
 * Misses are not sent immediately. They wait in a queue until the current tick
 * ends, then go out as one POST per observationReferenceDate and embed scope.
 * Calls made in the same React commit (a parent's effect and its children's, or
 * N gauges reacting to one dashboard change) share a single request.
 *
 * Each entry holds a promise, stored as soon as the request is queued, so
 * callers that ask while it's in flight wait on the same request.
 */

const CACHE_TTL_MS = 10 * 60 * 1000;
// After a refresh fails, the old data stays and is retried this much later.
const RETRY_AFTER_MS = 60 * 1000;
const MAX_ENTRIES = 500;
// /api/series refuses bodies with more requests than this.
const MAX_REQUESTS_PER_POST = 200;

type Settled = { result: SeriesResult; metadata: CacheRequestMetadata };

type CacheEntry = {
  promise: Promise<Settled>;
  settled?: Settled;
  fetchedAt: number;
  // While an expired entry refreshes, the data it had, still shown to gauges.
  stale?: Settled;
};

type QueuedRequest = {
  key: string;
  request: SeriesCacheRequest;
  entry: CacheEntry;
  resolve: (settled: Settled) => void;
  reject: (error: unknown) => void;
};

type QueueGroup = {
  observationReferenceDate?: string;
  embed?: SeriesEmbedScope;
  items: QueuedRequest[];
};

const entries = new Map<string, CacheEntry>();
const listeners = new Set<() => void>();
// Queued misses grouped by everything that applies to a whole POST: the
// observationReferenceDate and the embed scope.
const queue = new Map<string, QueueGroup>();
let flushScheduled = false;

const reportResultError = (result: RawSeriesResult) => {
  posthog.capture("series_cache_error", {
    category: result.category,
    details: result.details,
    display_id: result.displayId,
    date: result.date ?? result.latestObservationDate ?? null,
    vintage_date: result.vintageDate ?? null,
    source: "seriesCacheClient",
    request_id: result.requestId,
    env: process.env.NODE_ENV,
  });
};

const scopeKey = (embed?: SeriesEmbedScope) => (embed ? `${embed.kind}:${embed.slug}` : "");

/**
 * The cache key for one request at one dashboard observation date, on one
 * shared link (if any). A shared link can be served at a different access
 * level than the viewer's own, so its answers are kept apart.
 */
export const entryKey = (request: SeriesCacheRequest, observationReferenceDate?: string, embed?: SeriesEmbedScope) =>
  `${buildCacheKey([request])}||obsDate:${observationReferenceDate ?? "latest"}` +
  (embed ? `||embed:${scopeKey(embed)}` : "");

const isExpired = (entry: CacheEntry) => !!entry.settled && Date.now() - entry.fetchedAt > CACHE_TTL_MS;

const evictOverflow = () => {
  // Map iterates in insertion order, so the first keys are the oldest.
  for (const key of entries.keys()) {
    if (entries.size <= MAX_ENTRIES) return;
    entries.delete(key);
  }
};

export const clearSeriesCache = () => {
  entries.clear();
  notify();
};

/**
 * Calls `listener` whenever a request settles (with data, a failed row or an
 * error) or an entry is primed or cleared, so readers of `peekEntry` can look
 * again. Returns the unsubscribe.
 */
export const subscribeSeriesCache = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const notify = () => listeners.forEach((listener) => listener());

/**
 * The data stored under this `entryKey`, with the metadata of the request that
 * fetched it (for support reports), or undefined. Never fetches.
 *
 * Data past its expiry is still returned (stale-while-revalidate), so a gauge
 * keeps showing it; `needsRefresh` says when to ask for fresh data.
 */
export const peekEntry = (key: string): Settled | undefined => {
  const entry = entries.get(key);
  return entry?.settled ?? entry?.stale;
};

/** Whether this key has finished data that's past its expiry and isn't already refreshing. */
export const needsRefresh = (key: string): boolean => {
  const entry = entries.get(key);
  return !!entry && isExpired(entry);
};

/** `peekEntry` for a request rather than a precomputed key. */
export const peekSeries = (request: SeriesCacheRequest, observationReferenceDate?: string, embed?: SeriesEmbedScope) =>
  peekEntry(entryKey(request, observationReferenceDate, embed));

/**
 * Store a result fetched outside this cache (a public link's first load, which
 * runs on the server) under the exact request it answers, so gauges find it
 * instead of fetching it again. Failed rows aren't stored.
 */
export const primeSeriesResult = (request: SeriesCacheRequest, result: SeriesResult, embed?: SeriesEmbedScope) => {
  if (isFailedResult(result)) return;
  const settled = { result, metadata: { request: "", cfRay: "" } };
  entries.set(entryKey(request, undefined, embed), {
    promise: Promise.resolve(settled),
    settled,
    fetchedAt: Date.now(),
  });
  evictOverflow();
  notify();
};

const JSON_HEADERS = { "content-type": "application/json", accept: "application/json" };

/**
 * Where a POST goes.
 *
 * - **Signed-in viewer, no public link:** straight from the browser to the
 *   cache service on their own token, so it's served from the Cloudflare edge
 *   nearest the viewer with no extra hop.
 * - **Public link (gauge embed or shared dashboard), or no session:** through
 *   /api/series, which attaches the embed service account on the server and
 *   checks the request against the link (or answers 401 with no link).
 */
const routeFor = async (embed: SeriesEmbedScope | undefined) => {
  if (!embed) {
    const {
      data: { session },
    } = await createClient().auth.getSession();
    if (session?.access_token) {
      return { url: CACHE_ENDPOINT, headers: cacheServiceHeaders(session.access_token) };
    }
  }
  return { url: SERIES_ENDPOINT, headers: JSON_HEADERS };
};

async function postSeriesRequests(
  normalizedRequests: ReturnType<typeof normalizeRequests>,
  observationReferenceDate: string | undefined,
  embed: SeriesEmbedScope | undefined,
): Promise<{ results: SeriesResult[]; metadata: CacheRequestMetadata }> {
  const { url, headers } = await routeFor(embed);
  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify({
      observationReferenceDate,
      requests: normalizedRequests,
      ...(embed ? { embed } : {}),
    }),
  });

  const cfRay = response.headers.get("Cf-Ray") ?? "";

  if (!response.ok) {
    const details = await response.text().catch(() => "");
    throw new Error(details || `Failed to fetch series data (${response.status})`);
  }

  const cacheJson = await response.json().catch(() => ({}));

  return {
    results: parseCacheResponse(cacheJson, reportResultError),
    metadata: { request: JSON.stringify(normalizedRequests), cfRay },
  };
}

// buildRequestId leaves out referenceDates, so two queued requests can share an
// id. Suffix repeats so every row in the response maps back to one request.
const withUniqueRequestIds = (requests: SeriesCacheRequest[]) => {
  const seen = new Map<string, number>();
  return normalizeRequests(requests).map((req) => {
    const count = seen.get(req.requestId) ?? 0;
    seen.set(req.requestId, count + 1);
    return count === 0 ? req : { ...req, requestId: `${req.requestId} - #${count + 1}` };
  });
};

// A request failed. If the entry was refreshing data it already had, keep that
// data (and retry later) rather than replacing it with the failure. Otherwise
// drop the entry so the next ask retries.
const keepOrDrop = (item: QueuedRequest) => {
  if (entries.get(item.key) !== item.entry) return;
  const { stale } = item.entry;
  if (!stale) {
    entries.delete(item.key);
    return;
  }
  item.entry.settled = stale;
  item.entry.stale = undefined;
  item.entry.promise = Promise.resolve(stale);
  item.entry.fetchedAt = Date.now() - CACHE_TTL_MS + RETRY_AFTER_MS;
};

const settle = (item: QueuedRequest, settled: Settled) => {
  if (entries.get(item.key) === item.entry) {
    // Failed rows are returned to their callers but never kept, so the next ask retries.
    if (isFailedResult(settled.result)) {
      keepOrDrop(item);
    } else {
      item.entry.stale = undefined;
      item.entry.settled = settled;
      item.entry.fetchedAt = Date.now();
    }
  }
  item.resolve(settled);
  notify();
};

async function sendGroup(items: QueuedRequest[], observationReferenceDate?: string, embed?: SeriesEmbedScope) {
  const normalized = withUniqueRequestIds(items.map((item) => item.request));

  try {
    const { results, metadata } = await postSeriesRequests(normalized, observationReferenceDate, embed);

    const byRequestId = new Map(results.filter((r) => r.requestId).map((r) => [r.requestId!, r]));
    const sentIds = new Set(normalized.map((n) => n.requestId));
    const unmatched = results.filter((r) => !r.requestId || !sentIds.has(r.requestId));

    items.forEach((item, index) => {
      const { displayId, variant } = item.request;
      let result = byRequestId.get(normalized[index].requestId);

      // Fallback for rows whose requestId doesn't match ours (a public link's
      // requests are rebuilt on the server): same gauge, preferring the same
      // variant. Each row is used once.
      if (!result) {
        const candidates = unmatched.filter((r) => r.gaugeData.display_id === displayId);
        result = candidates.find((r) => r.gaugeConfig.variant === variant) ?? candidates[0];
        if (result) unmatched.splice(unmatched.indexOf(result), 1);
      }

      settle(item, {
        result:
          result ?? buildErrorResult(displayId, { category: "unexpected", details: "No data returned for this gauge" }),
        metadata,
      });
    });
  } catch (error) {
    items.forEach((item) => {
      keepOrDrop(item);
      item.reject(error);
    });
    notify();
  }
}

// How a queued group is split into POSTs. A gauge embed's link serves each
// series at most once per request (its gauge, or a composite's subcomponents),
// so its requests are packed into POSTs with no display id repeated.
const toBatches = (group: QueueGroup): QueuedRequest[][] => {
  const batches: QueuedRequest[][] = [];
  if (group.embed?.kind === "gauge") {
    group.items.forEach((item) => {
      const open = batches.find(
        (batch) =>
          batch.length < MAX_REQUESTS_PER_POST && !batch.some((b) => b.request.displayId === item.request.displayId),
      );
      if (open) open.push(item);
      else batches.push([item]);
    });
    return batches;
  }
  for (let i = 0; i < group.items.length; i += MAX_REQUESTS_PER_POST) {
    batches.push(group.items.slice(i, i + MAX_REQUESTS_PER_POST));
  }
  return batches;
};

const flush = () => {
  flushScheduled = false;
  const groups = Array.from(queue.values());
  queue.clear();
  groups.forEach((group) => {
    toBatches(group).forEach((batch) => {
      void sendGroup(batch, group.observationReferenceDate, group.embed);
    });
  });
};

const enqueue = (
  key: string,
  request: SeriesCacheRequest,
  observationReferenceDate?: string,
  embed?: SeriesEmbedScope,
  stale?: Settled,
) => {
  let resolve!: QueuedRequest["resolve"];
  let reject!: QueuedRequest["reject"];
  const promise = new Promise<Settled>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  const entry: CacheEntry = { promise, fetchedAt: Date.now(), ...(stale ? { stale } : {}) };
  entries.set(key, entry);
  evictOverflow();

  const groupKey = `${observationReferenceDate ?? ""}|${scopeKey(embed)}`;
  const group = queue.get(groupKey) ?? { observationReferenceDate, embed, items: [] };
  group.items.push({ key, request, entry, resolve, reject });
  queue.set(groupKey, group);

  if (!flushScheduled) {
    flushScheduled = true;
    setTimeout(flush, 0);
  }
  return promise;
};

export async function fetchSeriesRows(
  requests: SeriesCacheRequest[],
  options?: {
    // --- Client-side cache controls ---
    force?: boolean;
    // --- Batch-level API params ---
    observationReferenceDate?: string;
    // --- Logged-out viewers on a public shared link ---
    embed?: SeriesEmbedScope;
  },
): Promise<FetchSeriesResult> {
  if (!requests.length) {
    return { results: [], metadata: { request: JSON.stringify(requests), cfRay: "" } };
  }

  const obsRefDate = options?.observationReferenceDate;
  const embed = options?.embed;
  const forced = new Set<string>();

  const pending = requests.map((request) => {
    const key = entryKey(request, obsRefDate, embed);
    // Force once per key per call, so duplicates in one call share the new fetch.
    if (options?.force && !forced.has(key)) {
      forced.add(key);
      entries.delete(key);
    }
    const existing = entries.get(key);
    if (existing && !isExpired(existing)) return existing.promise;
    // Expired: refresh it, keeping its data on show until the refresh lands.
    return enqueue(key, request, obsRefDate, embed, existing?.settled);
  });

  const settled = await Promise.all(pending);
  const cfRays = Array.from(new Set(settled.map((s) => s.metadata.cfRay).filter(Boolean)));

  return {
    // In request order, one result per request.
    results: settled.map((s) => s.result),
    metadata: { request: JSON.stringify(normalizeRequests(requests)), cfRay: cfRays.join(", ") },
  };
}

/**
 * Where a gauge's data comes from. By default the series cache on the viewer's
 * own access; a public link passes one that adds its scope (and its saved view)
 * to every request.
 *
 * `embed` and `resolveRequest` describe that transformation, so a gauge can
 * look up the exact cache entry the fetcher will store under, instead of
 * guessing from a request that's missing the link's saved date.
 */
export type SeriesFetcher = typeof fetchSeriesRows & {
  embed?: SeriesEmbedScope;
  resolveRequest?: (request: SeriesCacheRequest) => SeriesCacheRequest;
};
